// Pure helpers for workspace-level state: "is there work here worth
// protecting?", "has it changed since the last save/load?", and "what
// does a brand-new empty session look like?".
//
// Pure: no React, no DOM, no network. Not yet wired into the app.
import { DEFAULT_FIELD_COLOR } from "../constants/pitchObjects";

export const EMPTY_BOARD = Object.freeze({ objects: [], arrows: [], shapes: [], texts: [], lines: [] });

export const DEFAULT_ACTIVITY = Object.freeze({
  name: "",
  rules: "",
  time: "",
  sets: "",
  rest: "",
  coachingPoints: [],
  rotations: [],
});

export const EMPTY_SESSION_META = Object.freeze({
  date: "",
  startTime: "",
  location: "",
  objective: "",
  load: "",
  offensivePrinciples: [],
  defensivePrinciples: [],
});

// A fresh, empty workspace tab entry (same shape TrainingGround uses).
export const makeEmptyEntry = (tabId) => ({
  tabId,
  activity: { ...DEFAULT_ACTIVITY, coachingPoints: [], rotations: [] },
  activityId: null,
  pitchMode: "vertical-full",
  fieldColor: DEFAULT_FIELD_COLOR,
  showDimensions: true,
  board: { objects: [], arrows: [], shapes: [], texts: [], lines: [] },
});

// Product decision #1: "New" starts a completely new, empty session.
export function createFreshWorkspace(makeTabId) {
  const entry = makeEmptyEntry(makeTabId());
  return {
    tabs: [entry],
    activeTabId: entry.tabId,
    sessionId: null,
    sessionName: "",
    sessionMeta: { ...EMPTY_SESSION_META, offensivePrinciples: [], defensivePrinciples: [] },
  };
}

// Extracted from TrainingGround.isMeaningfulEntry (tab-delete confirm).
// One deliberate extension: rotations now count as content, so a tab
// holding only rotations is protected too.
export function isMeaningfulEntry(entry) {
  if (!entry) return false;
  const b = entry.board || {};
  if ((b.objects && b.objects.length) ||
      (b.arrows && b.arrows.length) ||
      (b.shapes && b.shapes.length) ||
      (b.texts && b.texts.length) ||
      (b.lines && b.lines.length)) return true;
  const a = entry.activity || {};
  if ((a.name || "").trim()) return true;
  if ((a.rules || "").trim()) return true;
  if (Array.isArray(a.coachingPoints) && a.coachingPoints.length) return true;
  if (Array.isArray(a.rotations) && a.rotations.length) return true;
  if ((a.time || "") !== "" || (a.sets || "") !== "" || (a.rest || "") !== "") return true;
  return false;
}

const metaHasContent = (m) => {
  if (!m) return false;
  return ["date", "startTime", "location", "objective", "load"].some((k) => (m[k] || "").toString().trim() !== "") ||
    (Array.isArray(m.offensivePrinciples) && m.offensivePrinciples.length > 0) ||
    (Array.isArray(m.defensivePrinciples) && m.defensivePrinciples.length > 0);
};

// Anything in the workspace a coach would be upset to lose.
export function isWorkspaceMeaningful({ tabs, sessionName, sessionMeta } = {}) {
  if ((tabs || []).some(isMeaningfulEntry)) return true;
  if ((sessionName || "").trim()) return true;
  return metaHasContent(sessionMeta);
}

// Deterministic JSON (object keys sorted) so two equal snapshots always
// produce the same fingerprint regardless of key insertion order.
export function stableStringify(value) {
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(",")}]`;
  if (value && typeof value === "object") {
    return `{${Object.keys(value)
      .filter((k) => value[k] !== undefined)
      .sort()
      .map((k) => `${JSON.stringify(k)}:${stableStringify(value[k])}`)
      .join(",")}}`;
  }
  return JSON.stringify(value === undefined ? null : value);
}

// Fingerprint of a buildSessionSnapshot() result. Captured as the
// baseline after load / save / new; compared on demand (beforeunload,
// before destructive actions) — never on every render.
export const workspaceFingerprint = (snapshot) => stableStringify(snapshot || null);

export function isWorkspaceDirty(currentSnapshot, baselineFingerprint) {
  return workspaceFingerprint(currentSnapshot) !== baselineFingerprint;
}

// Should a destructive action (New, Load Session, reload/close) warn?
// Only when there is meaningful content AND it differs from what was
// last saved/loaded.
export function shouldWarnBeforeDiscard({ workspace, currentSnapshot, baselineFingerprint }) {
  return isWorkspaceMeaningful(workspace) && isWorkspaceDirty(currentSnapshot, baselineFingerprint);
}
